import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, AutoFixHigh, OpenInNew, Refresh } from '@mui/icons-material';
import {
  materialTakeoffService,
  isAnalyzableAttachment,
  jobStatusColor,
  jobStatusLabel,
  takeoffStatusColor,
  takeoffStatusLabel,
} from '../services/materialTakeoffService';
import { realtimeService } from '../services/realtimeService';

export default function QuoteMaterialTakeoffsPanel({ quoteId, quoteNumber, attachments = [] }) {
  const navigate = useNavigate();
  const [takeoffs, setTakeoffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
  const pollRef = useRef(null);

  const analyzableAttachments = attachments.filter(isAnalyzableAttachment);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quoteId) return;
    try {
      if (!quiet) setLoading(true);
      setError('');
      const res = await materialTakeoffService.list({ quote_id: quoteId });
      setTakeoffs(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load material takeoffs');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const unsub = realtimeService.subscribe((event) => {
      if (event?.entity === 'quote' && String(event.entityId) === String(quoteId)) {
        if (event?.action?.includes('material_takeoff')) {
          load({ quiet: true });
        }
      }
    });
    return unsub;
  }, [quoteId, load]);

  const anyJobRunning = takeoffs.some(
    (t) => t.latest_job_status === 'queued' || t.latest_job_status === 'processing'
  );

  useEffect(() => {
    if (!anyJobRunning) {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = window.setInterval(() => load({ quiet: true }), 4000);
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [anyJobRunning, load]);

  const openCreateDialog = () => {
    setTitle(quoteNumber ? `Takeoff — ${quoteNumber}` : 'Material takeoff');
    setSelectedAttachmentIds(analyzableAttachments.map((a) => a.id));
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (selectedAttachmentIds.length === 0) {
      setError('Select at least one drawing attachment');
      return;
    }
    try {
      setCreating(true);
      setError('');
      const res = await materialTakeoffService.create({
        quote_id: quoteId,
        title: title.trim(),
        attachment_ids: selectedAttachmentIds,
      });
      setCreateOpen(false);
      await load({ quiet: true });
      const created = res.data;
      if (created?.id) {
        await materialTakeoffService.analyze(created.id);
        navigate(`/material-takeoffs/${created.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create material takeoff');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
          Material takeoffs
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<Refresh />} onClick={() => load()} disabled={loading}>
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={analyzableAttachments.length === 0}
            onClick={openCreateDialog}
          >
            New takeoff
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Create a material takeoff from quote drawing attachments. Analysis produces a buy list, nesting review, sheet summary, and red flags.
      </Typography>

      {analyzableAttachments.length === 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Upload PDF or image drawings on the Attachments tab first.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : takeoffs.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No material takeoffs for this quote yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Analysis</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {takeoffs.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>
                    <Chip size="small" label={takeoffStatusLabel(row.status)} color={takeoffStatusColor(row.status)} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {row.latest_job_status ? (
                      <Tooltip title={row.latest_job_error || ''}>
                        <Chip
                          size="small"
                          label={jobStatusLabel(row.latest_job_status)}
                          color={jobStatusColor(row.latest_job_status)}
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<OpenInNew />}
                      component={RouterLink}
                      to={`/material-takeoffs/${row.id}`}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New material takeoff</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Typography variant="subtitle2" gutterBottom>Drawing attachments</Typography>
          {analyzableAttachments.map((att) => (
            <Box key={att.id} sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                size="small"
                checked={selectedAttachmentIds.includes(att.id)}
                onChange={(e) => {
                  setSelectedAttachmentIds((prev) => (
                    e.target.checked
                      ? [...prev, att.id]
                      : prev.filter((id) => id !== att.id)
                  ));
                }}
              />
              <Typography variant="body2">{att.file_name}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <AutoFixHigh />}
            disabled={creating || selectedAttachmentIds.length === 0}
            onClick={handleCreate}
          >
            Create &amp; analyze
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
